import React, { Component, Fragment } from 'react'
import { connect } from 'react-redux'
import { History } from 'history'

import { fetchUser, updateUser, disableUser } from '../users/userActions'
import { selectUser } from '../../reducers/users'
import AdminForm, { UUID_REGEX, trimAgentId } from './adminForm'
import { fetchOrganizations } from '../../actions/organizationActions'
import { displayNotification } from '../../actions/notificationsActions'
import { User } from '../../core/types'
import Breadcrumb from '../../components/Breadcrumb'
import LoadingSection from '../../components/loadingSection'
import { BadgeGate } from '../common/permissions/badgeGate'

export type Callback<T = void> = (...args: any) => T
export interface Match<T> {
  params: T
}

export interface ParamsID {
  id: string
}

export interface Props {
  user: User
  match: Match<ParamsID>
  fetchUser: Callback
  updateUser: Callback<Promise<any>>
  disableUser: Callback
  fetchOrganizations: Callback<Promise<any>>
  displayNotification?: Callback
  organizations: any[]
  location?: { search: string }
  history: History
}

// Edit Admin's save messages are sentence-length (validation errors such as a
// taken email or phone), and the shared 3-second default cleared them before
// they could be read. Only this screen uses the longer time.
export const EDIT_ADMIN_NOTIFICATION_MS = 5000

export class AdminEdit extends Component<Props> {
  static displayName = 'AdminEdit'

  componentWillMount() {
    const { fetchUser, match, user, fetchOrganizations } = this.props
    if (!user.id) {
      fetchUser(match.params.id)
    }
    fetchOrganizations()
  }

  // Opened from the dialer error page (its fix button adds the query param):
  // "back" is that error page, which still shows the old failure. Land on the
  // saved record instead and say what to do next.
  fromDialerErrorPage = () =>
    new URLSearchParams(this.props.location?.search || '').has(
      'calltoolsAgentId'
    )

  onSubmit = (values) => {
    const { history, updateUser, displayNotification, user } = this.props

    // Always sent, trimmed: redux-form drops a cleared field from `values`, so
    // without this an emptied Agent ID would silently stay as it was.
    return updateUser(
      {
        ...values,
        calltoolsAgentId: trimAgentId(values.calltoolsAgentId),
      },
      { notificationDuration: EDIT_ADMIN_NOTIFICATION_MS }
    ).then(() => {
      if (this.fromDialerErrorPage()) {
        displayNotification?.(
          'Agent ID saved. Start the call action again from your dialer.',
          'success',
          EDIT_ADMIN_NOTIFICATION_MS
        )
        history.push(`/admins/${user.id}`)
      } else {
        history.goBack()
      }
    })
  }

  backToIndex = () => {
    this.props.history.goBack()
  }

  disableUserOnClick = (id) => {
    const { disableUser } = this.props
    if (window.confirm('Are you sure you want to disable this admin?')) {
      disableUser(id)
      this.backToIndex()
    }
  }

  render() {
    const { user, organizations } = this.props
    const {
      id,
      firstName,
      lastName,
      email,
      phone,
      zendeskExternalId,
      calltoolsAgentId,
      enabled,
      organizationId,
    } = user as any

    // The dialer error page links here with the unlinked agent id. Prefilled
    // only into an empty field: a user who already has an id is not the
    // agent that page was about, and their id must not be swapped silently.
    const suggestedAgentId = new URLSearchParams(
      this.props.location?.search || ''
    ).get('calltoolsAgentId')
    const prefillAgentId =
      !calltoolsAgentId && suggestedAgentId && UUID_REGEX.test(suggestedAgentId)
        ? suggestedAgentId
        : calltoolsAgentId

    const initialValues = {
      id,
      firstName,
      lastName,
      email,
      phone,
      zendeskExternalId,
      calltoolsAgentId: prefillAgentId,
      organizationId,
    }

    const levels = [
      { label: 'Index ', path: '/' },
      { label: 'Admins', path: '/admins' },
      { label: 'Edit', path: '' },
    ]

    return (
      <Fragment>
        <Breadcrumb levels={levels} />
        <LoadingSection isLoading={!user.id}>
          <div className="user-profile-edit">
            {user.id && (
              <AdminForm
                onSubmit={this.onSubmit}
                initialValues={initialValues}
                organizations={organizations}
                cancel={this.backToIndex}
                title="Edit Admin"
                organizationLocked={Boolean(organizationId)}
              />
            )}
          </div>
          <div className="user-profile-edit-disable">
            <BadgeGate disableUI requiredBadges={['CAN_DISABLE_USERS']}>
              <button
                onClick={() => this.disableUserOnClick(user.id)}
                data-testid="disable-admin-button"
                className="button button--primary-round user-profile-edit__form__button--submit"
                disabled={!enabled}
                type="button"
              >
                Disable Admin
              </button>
            </BadgeGate>
          </div>
        </LoadingSection>
      </Fragment>
    )
  }
}

const mapStateToProps = (state, { match }) => ({
  user: selectUser(state.users, match.params.id),
  organizations: (state.organizations && state.organizations.all) || [],
})

export default connect(mapStateToProps, {
  fetchUser,
  updateUser,
  disableUser,
  fetchOrganizations,
  displayNotification,
})(AdminEdit)
